import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

export function AuthGate({
  children,
}: {
  children: (session: Session) => ReactNode;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setReady(true);
      return;
    }
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) =>
      setSession(next),
    );
    return () => data.subscription.unsubscribe();
  }, []);

  async function signIn(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    setBusy(true);
    setError(null);
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.error) {
      setError("Email or password could not be verified.");
      setPassword("");
    }
    setBusy(false);
  }

  if (!ready)
    return (
      <div className="center-state" role="status">
        <span className="spinner" />
        Checking access…
      </div>
    );
  if (!supabase)
    return (
      <div className="center-state">
        <strong>Configuration required</strong>
        <p>
          Add the browser-safe Supabase URL and publishable key to start
          SideKick.
        </p>
      </div>
    );
  if (session) return <>{children(session)}</>;
  return (
    <main className="signin-shell">
      <section className="signin-copy">
        <p className="eyebrow">SOURCEENERGY / GOVERNED EXECUTION</p>
        <h1>Move approved intent into accountable work.</h1>
        <p>
          SideKick coordinates evidence, organizations, and delivery without
          manufacturing authority.
        </p>
      </section>
      <form className="signin-card" noValidate onSubmit={signIn}>
        <p className="eyebrow">OPERATOR ACCESS</p>
        <h2>Sign in to SideKick</h2>
        {error && (
          <div className="alert alert--error" role="alert">
            {error}
          </div>
        )}
        <label>
          Email
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </label>
        <button
          className="button button--primary"
          type="submit"
          disabled={busy || !email || !password}
        >
          <span>{busy ? "Signing in…" : "Sign in"}</span>
        </button>
        <p className="fine-print">
          Access is governed by SourceEnergy One identity and SETC role
          assignments.
        </p>
      </form>
    </main>
  );
}
